/**
 * Read-through, stale-while-revalidate cache for the product listing call.
 *
 * WHY THIS ISN'T A SERVICE-WORKER CACHE: `get_product_filter_data` goes out
 * as a POST (frappe.call defaults to POST — see frappe/public/js/frappe/
 * request.js — and no call site here overrides that), with the filters in
 * the body rather than the URL. The Cache API keys on the Request, so every
 * distinct filter/page combination sharing one POST URL would collide under
 * a naive `caches.match`. IndexedDB has no such constraint, doesn't require
 * a secure context (Cache API does — window.caches is undefined entirely
 * over plain HTTP on a non-localhost host, unlike IndexedDB), and is the
 * same storage engine the checkout offline queue already uses.
 *
 * Behavior: a cached entry younger than MAX_AGE_MS is returned immediately
 * (stale-while-revalidate) — the caller is expected to kick a background
 * refetch itself and let it repopulate the cache for the next visit.
 *
 * Price, discount and stock are NEVER part of what's stored here — see
 * DYNAMIC_FIELDS below. Those change under the shopper without a page
 * reload (a Hub restock, a pricing-rule edit, another dealer buying the
 * last unit) and must read as live on every single view, cache hit or not.
 * Everything else about a listing row — name, image, route, brand,
 * description — is genuinely static for the cache's 15-minute window, so
 * only that half is written to IndexedDB; the caller re-fetches the
 * dynamic half through get_item_live_fields on every cache hit.
 */

/** Stripped out of every cached row; always re-fetched live (see webshop_source.js's
 * mergeLiveFields / raftor_webhq.catalog.api.get_item_live_fields). */
export const DYNAMIC_FIELDS = [
	'price_list_rate',
	'formatted_price',
	'formatted_mrp',
	'discount_percent',
	'discount',
	'price_is_from',
	'in_stock',
	'stock_qty',
	'in_cart',
	'wished',
];

const DB_NAME = 'raftor-catalog-cache';
const DB_VERSION = 1;
const STORE_NAME = 'listings';
const MAX_AGE_MS = 15 * 60 * 1000;
const MAX_ENTRIES = 40;

let dbPromise = null;

function openDb() {
	if (dbPromise) return dbPromise;
	dbPromise = new Promise((resolve, reject) => {
		if (typeof indexedDB === 'undefined') {
			reject(new Error('indexedDB unavailable'));
			return;
		}
		const req = indexedDB.open(DB_NAME, DB_VERSION);
		req.onupgradeneeded = () => {
			const db = req.result;
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				db.createObjectStore(STORE_NAME, { keyPath: 'key' }).createIndex('cachedAt', 'cachedAt');
			}
		};
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error);
	});
	return dbPromise;
}

/** Stable key across differently-ordered filter objects. */
export function cacheKey(args) {
	const sorted = (obj) => {
		if (!obj || typeof obj !== 'object') return obj;
		return Object.keys(obj)
			.sort()
			.reduce((acc, k) => {
				acc[k] = sorted(obj[k]);
				return acc;
			}, {});
	};
	return JSON.stringify(sorted(args));
}

/** Deep-clones a listing response with DYNAMIC_FIELDS removed from every item row. */
export function stripDynamicFields(listing) {
	if (!listing || !Array.isArray(listing.items)) return listing;
	return Object.assign({}, listing, {
		items: listing.items.map((item) => {
			const clean = Object.assign({}, item);
			DYNAMIC_FIELDS.forEach((field) => {
				delete clean[field];
			});
			return clean;
		}),
	});
}

/** Resolves the cached listing response, or null on a miss/error/stale entry. */
export async function getCachedListing(key) {
	try {
		const db = await openDb();
		return await new Promise((resolve) => {
			const tx = db.transaction(STORE_NAME, 'readonly');
			const req = tx.objectStore(STORE_NAME).get(key);
			req.onsuccess = () => {
				const row = req.result;
				if (!row || Date.now() - row.cachedAt > MAX_AGE_MS) {
					resolve(null);
					return;
				}
				resolve(row.response);
			};
			req.onerror = () => resolve(null);
		});
	} catch (err) {
		return null;
	}
}

/** Best-effort write; a caching failure must never surface to the shopper. */
export async function setCachedListing(key, response) {
	try {
		const db = await openDb();
		await new Promise((resolve) => {
			const tx = db.transaction(STORE_NAME, 'readwrite');
			const store = tx.objectStore(STORE_NAME);
			store.put({ key, response, cachedAt: Date.now() });
			tx.oncomplete = resolve;
			tx.onerror = resolve;
		});
		await evictOldest(db);
	} catch (err) {
		// Non-fatal — the listing itself already succeeded.
	}
}

async function evictOldest(db) {
	return new Promise((resolve) => {
		const tx = db.transaction(STORE_NAME, 'readwrite');
		const store = tx.objectStore(STORE_NAME);
		const countReq = store.count();
		countReq.onsuccess = () => {
			const overflow = countReq.result - MAX_ENTRIES;
			if (overflow <= 0) {
				resolve();
				return;
			}
			const index = store.index('cachedAt');
			let removed = 0;
			const cursorReq = index.openCursor();
			cursorReq.onsuccess = () => {
				const cursor = cursorReq.result;
				if (!cursor || removed >= overflow) {
					resolve();
					return;
				}
				cursor.delete();
				removed += 1;
				cursor.continue();
			};
			cursorReq.onerror = () => resolve();
		};
		countReq.onerror = () => resolve();
	});
}
